import { useCallback, useEffect, useState } from 'react';
import { useRevalidator } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { isDefined, isNotDefined, isOfType, sort } from '@hsp/utilities';

import {
  IExportStatusPayload,
  IStatusPayload,
  NotificationTarget,
  QuantityAudiencePayload,
  QuantityPayload,
} from '@c/notifications/types';

import { NlsDashboardQueryKeys } from '@p/nls-dashboard/hooks/queries/query-keys';

import { AudienceShopStatus } from '@t/api/audience';
import { IResult } from '@t/api/common';
import { NlsOrderStatus } from '@t/api/nls-order/enum/nls-order-status.enum';
import { INlsOrder } from '@t/api/nls-order/nls-order.model';

import { useSignalREffect } from 'hooks/signalr';

export const useNlsManagementBroadcasts = () => {
  const [reload, setReload] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const revalidator = useRevalidator();

  const handleQuantity = useCallback(
    (payload: QuantityPayload) => {
      const { audienceId, quantity, shopId, status } = payload;
      let quantityStatus: AudienceShopStatus | undefined;
      let total = 0;

      if (isDefined(audienceId)) {
        queryClient.setQueryData<IResult<INlsOrder[]>>(NlsDashboardQueryKeys.orders.all, (cached) => {
          if (isDefined(cached)) {
            const { dataContainer } = cached;

            if (isNotDefined(dataContainer)) {
              return cached;
            }

            const index = dataContainer.findIndex((item) => item.shopId === shopId);

            const order = dataContainer[index];

            // only update cache with shop/quantity status if it is not already being finalized or shipped
            if (
              order.nlsOrderStatus == NlsOrderStatus.FinalizeFailed ||
              order.nlsOrderStatus == NlsOrderStatus.FinalizePending ||
              order.nlsOrderStatus == NlsOrderStatus.Finalized ||
              order.nlsOrderStatus == NlsOrderStatus.Shipped ||
              order.nlsOrderStatus == NlsOrderStatus.ShippedFailed ||
              order.nlsOrderStatus == NlsOrderStatus.ShippedPending
            ) {
              return;
            }

            if (index >= 0) {
              cached = {
                ...cached,
                dataContainer: [
                  ...dataContainer.slice(0, index),
                  { ...dataContainer[index], nlsOrderStatus: status.valueOf(), quantity },
                  ...dataContainer.slice(index + 1),
                ],
              };
            }

            quantityStatus =
              sort(
                [
                  ...(cached.dataContainer ?? []).map(({ nlsOrderStatus }) => {
                    const currentStatus = nlsOrderStatus.valueOf() ?? AudienceShopStatus.QuantityPending;
                    let order = 2;

                    switch (currentStatus) {
                      case NlsOrderStatus.QuantityFailed:
                        order = 1;
                        break;

                      case NlsOrderStatus.QuantityPending:
                        order = 2;
                        break;

                      case NlsOrderStatus.QuantityUpdated:
                        order = 3;
                        break;
                    }

                    return {
                      order,
                      status: currentStatus,
                    };
                  }),
                ],
                { direction: 'asc', field: 'order' }
              ).shift()?.status ?? AudienceShopStatus.QuantityPending;

            return cached;
          }

          return cached;
        });

        queryClient.setQueriesData<IResult<INlsOrder[]>>({ queryKey: NlsDashboardQueryKeys.orders.all }, (cached) => {
          if (isDefined(cached)) {
            const { dataContainer } = cached;

            if (isNotDefined(dataContainer)) {
              return cached;
            }

            const index = dataContainer.findIndex((item) => item.audienceId === audienceId);

            const order = dataContainer[index];

            // only update cache with shop/quantity status if it is not already being finalized or shipped
            if (
              order.nlsOrderStatus == NlsOrderStatus.FinalizeFailed ||
              order.nlsOrderStatus == NlsOrderStatus.FinalizePending ||
              order.nlsOrderStatus == NlsOrderStatus.Finalized ||
              order.nlsOrderStatus == NlsOrderStatus.Shipped ||
              order.nlsOrderStatus == NlsOrderStatus.ShippedFailed ||
              order.nlsOrderStatus == NlsOrderStatus.ShippedPending
            ) {
              return;
            }

            if (index >= 0) {
              if (isNotDefined(quantityStatus)) {
                quantityStatus = status;
                total = quantity;
              }

              cached = {
                ...cached,
                dataContainer: [
                  ...dataContainer.slice(0, index),
                  {
                    ...dataContainer[index],
                    nlsOrderStatus: quantityStatus.valueOf(),
                    quantity: total,
                  },
                  ...dataContainer.slice(index + 1),
                ],
              };
            }

            return cached;
          }

          return cached;
        });

        queryClient.setQueryData<IResult<INlsOrder>>(NlsDashboardQueryKeys.byId(shopId), (cached) => {
          if (isDefined(cached)) {
            const { dataContainer } = cached;

            if (isNotDefined(dataContainer)) {
              return cached;
            }

            const { shopId: cachedShopId } = dataContainer;

            const order = dataContainer;

            // only update cache with shop/quantity status if it is not already being finalized or shipped
            if (
              order.nlsOrderStatus == NlsOrderStatus.FinalizeFailed ||
              order.nlsOrderStatus == NlsOrderStatus.FinalizePending ||
              order.nlsOrderStatus == NlsOrderStatus.Finalized ||
              order.nlsOrderStatus == NlsOrderStatus.Shipped ||
              order.nlsOrderStatus == NlsOrderStatus.ShippedFailed ||
              order.nlsOrderStatus == NlsOrderStatus.ShippedPending
            ) {
              return;
            }

            if (cachedShopId === shopId) {
              cached = {
                ...cached,
                dataContainer: {
                  ...dataContainer,
                  nlsOrderStatus: status.valueOf(),
                  quantity,
                },
              };

              setReload(true);
            }
          }

          return cached;
        });
      }
    },
    [queryClient]
  );

  const handleQuantityAudience = useCallback(
    (payload: QuantityAudiencePayload) => {
      const { audienceId, quantity, status } = payload;

      queryClient.setQueriesData<IResult<INlsOrder[]>>({ queryKey: NlsDashboardQueryKeys.orders.all }, (cached) => {
        if (isDefined(cached)) {
          const { dataContainer } = cached;

          if (isNotDefined(dataContainer)) {
            return cached;
          }

          const index = dataContainer.findIndex((item) => item.audienceId === audienceId);

          const order = dataContainer[index];

          // only update cache with shop/quantity status if it is not already being finalized or shipped
          if (
            order.nlsOrderStatus == NlsOrderStatus.FinalizeFailed ||
            order.nlsOrderStatus == NlsOrderStatus.FinalizePending ||
            order.nlsOrderStatus == NlsOrderStatus.Finalized ||
            order.nlsOrderStatus == NlsOrderStatus.Shipped ||
            order.nlsOrderStatus == NlsOrderStatus.ShippedFailed ||
            order.nlsOrderStatus == NlsOrderStatus.ShippedPending
          ) {
            return;
          }

          if (index >= 0) {
            cached = {
              ...cached,
              dataContainer: [
                ...dataContainer.slice(0, index),
                {
                  ...dataContainer[index],
                  nlsOrderStatus: status.valueOf(),
                  quantity,
                },
                ...dataContainer.slice(index + 1),
              ],
            };
          }

          return cached;
        }

        return cached;
      });
    },
    [queryClient]
  );

  const handleStatus = useCallback(
    (payload: IStatusPayload) => {
      const { id, status } = payload;

      const statusValue = status.valueOf();
      // have to map audience status to nls order status which is a combo of shop and audience status
      const nlsStatus = statusValue + 2;

      queryClient.setQueriesData<IResult<INlsOrder[]>>({ queryKey: NlsDashboardQueryKeys.orders.all }, (cached) => {
        if (isDefined(cached)) {
          const { dataContainer } = cached;

          if (isNotDefined(dataContainer)) {
            return cached;
          }

          const index = dataContainer.findIndex((item) => item.audienceId === id);

          if (index >= 0) {
            const target = dataContainer[index];

            cached = {
              ...cached,
              dataContainer: [
                ...dataContainer.slice(0, index),
                {
                  ...target,
                  nlsOrderStatus: nlsStatus,
                  shippedDate: isOfType<IExportStatusPayload>(payload, 'shippedDate')
                    ? payload.shippedDate
                    : target.shippedDate,
                },
                ...dataContainer.slice(index + 1),
              ],
            };
          }

          return cached;
        }

        return cached;
      });

      queryClient.setQueryData<IResult<INlsOrder[]>>(NlsDashboardQueryKeys.byId(id), (cached) => {
        if (isDefined(cached)) {
          const { dataContainer } = cached;

          if (isNotDefined(dataContainer)) {
            return cached;
          }

          cached = {
            ...cached,
            dataContainer: dataContainer.map((item) => ({ ...item, statusId: status })),
          };

          return cached;
        }

        return cached;
      });
    },
    [queryClient]
  );

  useSignalREffect(
    NotificationTarget.AudienceStatus,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleStatus(payload);
    },
    []
  );

  useSignalREffect(
    NotificationTarget.Export,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleStatus(payload);
    },
    []
  );

  useSignalREffect(
    NotificationTarget.Quantity,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleQuantity(payload);
    },
    []
  );

  useSignalREffect(
    NotificationTarget.QuantityAudience,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleQuantityAudience(payload);
    },
    []
  );

  useEffect(() => {
    if (reload) {
      revalidator.revalidate();
      setReload(false);
    }
  }, [reload, revalidator]);
};
